import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaArrowLeft, FaAmazon, FaGoodreads, FaImages } from 'react-icons/fa';
import { API } from '../config/api';

const BookContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem;
`;

const BookContent = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
`;

const BookHeader = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 3rem;
  padding: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const BookCover = styled.div`
  width: 100%;
  height: 400px;
  background: ${props => props.src ? `url(${API}${props.src})` : 'linear-gradient(45deg, #667eea, #764ba2)'};
  background-size: cover;
  background-position: center;
  border-radius: 15px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
`;

const BookInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const BookTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 900;
  color: #2d3436;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const BookGenre = styled.span`
  display: inline-block;
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  align-self: flex-start;
  
  @media (max-width: 768px) {
    align-self: center;
  }
`;

const BookDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: #636e72;
  margin-bottom: 2rem;
`;

const PublishedDate = styled.p`
  font-size: 1rem;
  color: #b2bec3;
  margin-bottom: 2rem;
  font-weight: 600;
`;

const LinksContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const BookLink = styled(motion.a)`
  background: ${props => props.variant === 'amazon' ? '#ff9500' : '#8d6e63'};
  color: white;
  padding: 1rem 2rem;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const BlurbSection = styled.div`
  padding: 3rem;
  background: #f8f9fa;
`;

const BlurbTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #2d3436;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const BlurbText = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #2d3436;
  max-width: 800px;
  margin: 0 auto;
  white-space: pre-line;
`;

const BackButton = styled(motion.button)`
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LoadingContainer = styled.div`
  min-height: 50vh;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
  color: #636e72;
`;

const ErrorContainer = styled.div`
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: #d63031;
`;

const BookDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBook();
  }, [id]);

  const fetchBook = async () => {
    try {
      const response = await fetch(`${API}/books/${id}`);
      if (response.ok) {
        const data = await response.json();
        setBook(data);
      } else {
        setError('Book not found');
      }
    } catch (error) {
      setError('Failed to load book details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <BookContainer>
        <LoadingContainer>Loading book details...</LoadingContainer>
      </BookContainer>
    );
  }

  if (error) {
    return (
      <BookContainer>
        <ErrorContainer>
          <h2>{error}</h2>
          <BackButton
            onClick={() => navigate('/')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            ← Back to Home
          </BackButton>
        </ErrorContainer>
      </BookContainer>
    );
  }

  return (
    <BookContainer>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <BackButton
          onClick={() => navigate('/')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          ← Back to Home
        </BackButton>

        <BookContent
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <BookHeader>
            <BookCover src={book.coverImage}>
              {!book.coverImage && book.title}
            </BookCover>
            
            <BookInfo>
              <BookTitle>{book.title}</BookTitle>
              {book.genre && <BookGenre>{book.genre}</BookGenre>}
              <BookDescription>{book.description}</BookDescription>
              {book.publishedDate && (
                <PublishedDate>
                  Published: {formatDate(book.publishedDate)}
                </PublishedDate>
              )}
              
              <LinksContainer>
                {book.amazonLink && (
                  <BookLink
                    href={book.amazonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="amazon"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    📚 Buy on Amazon
                  </BookLink>
                )}
                {book.goodreadsLink && (
                  <BookLink
                    href={book.goodreadsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    ⭐ View on Goodreads
                  </BookLink>
                )}
              </LinksContainer>
            </BookInfo>
          </BookHeader>

          {book.blurb && (
            <BlurbSection>
              <BlurbTitle>About This Book</BlurbTitle>
              <BlurbText>{book.blurb}</BlurbText>
            </BlurbSection>
          )}
        </BookContent>
      </div>
    </BookContainer>
  );
};

export default BookDetail;
